import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapPin, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const favoriteIds = JSON.parse(localStorage.getItem("favorites") || "[]");
      
      if (favoriteIds.length === 0) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      // Fetch all listings and filter favorites
      const response = await axios.get(`${API}/listings`);
      const allListings = response.data;
      const favoriteListings = allListings.filter((listing) =>
        favoriteIds.includes(listing.id)
      );
      setFavorites(favoriteListings);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      toast.error("Kunne ikke laste inn favoritter");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = (listingId) => {
    const favoriteIds = JSON.parse(localStorage.getItem("favorites") || "[]");
    const updated = favoriteIds.filter((id) => id !== listingId);
    localStorage.setItem("favorites", JSON.stringify(updated));
    setFavorites(favorites.filter((listing) => listing.id !== listingId));
    toast.success("Fjernet fra favoritter");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
      {/* Navigation */}
      <Navbar showBackButton={true} showWedgeGuide={false} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-2">
            Mine favoritter
          </h2>
          <p className="text-gray-600">
            Annonser du har lagret for senere
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <p className="text-gray-500">Laster favoritter...</p>
          </div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg" data-testid="no-favorites">
            <Heart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">Du har ingen favoritter enda</p>
            <Button
              onClick={() => navigate("/")}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Utforsk annonser
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
            {favorites.map((listing) => (
              <Card
                key={listing.id}
                className="bg-white border border-gray-200 overflow-hidden"
                data-testid={`favorite-card-${listing.id}`}
              >
                <CardContent className="p-0">
                  <div
                    className="aspect-video bg-gray-200 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/listing/${listing.id}`)}
                  >
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-4xl">⛳</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3
                        className="font-semibold text-lg text-gray-900 flex-1 cursor-pointer hover:text-emerald-600"
                        onClick={() => navigate(`/listing/${listing.id}`)}
                        data-testid={`favorite-title-${listing.id}`}
                      >
                        {listing.title}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFavorite(listing.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-favorite-${listing.id}`}
                      >
                        <Heart className="w-5 h-5 fill-current" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {listing.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-emerald-600" data-testid={`favorite-price-${listing.id}`}>
                        {listing.price.toLocaleString('nb-NO')} kr
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1" />
                        {listing.location}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {listing.category}
                      </span>
                      <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded">
                        {listing.condition}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
